import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export function AuthPage({ mode = "login" }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(`Welcome${data.name ? `, ${data.name}` : ""}`);
      navigate(data.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <h1 className="font-heading text-3xl uppercase font-black tracking-tighter">{mode === "login" ? "Sign In" : "Create Account"}</h1>
      <p className="text-sm text-black/50 mt-2">Track orders, save gear, and build kits faster.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        {mode === "register" && (
          <input data-testid="auth-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
        )}
        <input data-testid="auth-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
        <input data-testid="auth-password-input" required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
        {error && <p data-testid="auth-error" className="text-xs text-[#8B0000] font-semibold">{error}</p>}
        <button data-testid="auth-submit-btn" type="submit" disabled={busy}
          className="w-full bg-[#121212] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150 disabled:opacity-50">
          {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-black/10" /><span className="text-[10px] uppercase tracking-widest text-black/40 font-bold">or</span><div className="flex-1 h-px bg-black/10" /></div>
      <button data-testid="google-login-btn" onClick={googleLogin}
        className="w-full border border-black py-4 text-sm font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-150">
        Continue with Google
      </button>
      <p className="text-sm text-black/50 mt-6 text-center">
        {mode === "login" ? <>No account? <Link data-testid="switch-to-register" to="/register" className="text-[#4A5D23] font-bold">Create one</Link></>
          : <>Already have an account? <Link data-testid="switch-to-login" to="/login" className="text-[#4A5D23] font-bold">Sign in</Link></>}
      </p>
    </div>
  );
}

export function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sessionId) { navigate("/login"); return; }
    api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/account", { state: { user: data }, replace: true });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [location.hash, navigate, setUser]);

  return <div className="py-32 text-center text-black/40 animate-pulse text-sm uppercase tracking-widest font-bold">Signing you in…</div>;
}
